import type {
  SheetColumnVisibility,
  TasksViewPreferences,
} from './tasksViewPreferences';

// Column widths in the category sheet (rem). Input name sits between
// NAME_MIN_REM and NAME_MAX_REM; Notes, as a column, needs NOTES_MIN_REM.
// TaskCategorySheet's colgroup classes match these.
export const SHEET_COLUMN_REM = {
  taskFull: 18,
  taskTrimmed: 13,
  rate: 5,
  rateUnit: 6,
  required: 6,
  costPerUnit: 6,
  costPerHa: 6.5,
};
const NAME_MIN_REM = 14;
const NAME_MAX_REM = 28;
const NOTES_MIN_REM = 16;

// Below this width the Task cell becomes a band above its products.
const STACK_BELOW_REM = 40;
// Below this width, with the Task column on `auto`, it trims itself.
const TRIM_TASK_COLUMN_BELOW_REM = 60;

// Folded first to last when Input name runs short of room.
const FOLD_ORDER: (keyof SheetColumnVisibility)[] = [
  'required',
  'costPerUnit',
  'rateUnit',
];

export type FoldedColumns = Pick<
  SheetColumnVisibility,
  'rateUnit' | 'required' | 'costPerUnit'
>;

export interface SheetLayout {
  /** Narrow: each Task cell is a band above its products, no Task column. */
  isStacked: boolean;
  /** The Task column is narrow (the user's choice, or a narrow sheet). */
  isTrimmed: boolean;
  /**
   * The user chose compact tasks (« in the Task header): one line per Task
   * cell, with Water rate and Target in its popover. A narrow sheet only
   * trims the column; it never hides details on its own.
   */
  isCompact: boolean;
  /** The value columns that show. */
  columns: SheetColumnVisibility;
  /** Columns View options shows but the width folded away; their values go under the product name. */
  folded: FoldedColumns;
  /** Wide: Notes is a column beside the products instead of a row under them. */
  notesAsColumn: boolean;
}

export const valueColumnsRem = (columns: SheetColumnVisibility) =>
  SHEET_COLUMN_REM.rate +
  (columns.rateUnit ? SHEET_COLUMN_REM.rateUnit : 0) +
  (columns.required ? SHEET_COLUMN_REM.required : 0) +
  (columns.costPerUnit ? SHEET_COLUMN_REM.costPerUnit : 0) +
  (columns.costPerHa ? SHEET_COLUMN_REM.costPerHa : 0);

/**
 * Which of Required, Cost /unit and Rate unit to fold away (in that order)
 * so Input name keeps at least 14 rem of `availableRem` — the width left for
 * Input name and the value columns. Shared by the sheet and the original
 * layout's product table (OP-2556).
 */
export const foldColumnsForWidth = (
  availableRem: number,
  columns: SheetColumnVisibility
): SheetColumnVisibility => {
  const fitted = { ...columns };
  FOLD_ORDER.forEach((key) => {
    if (fitted[key] && availableRem - valueColumnsRem(fitted) < NAME_MIN_REM) {
      fitted[key] = false;
    }
  });
  return fitted;
};

/**
 * The category sheet's layout for its own width (OP-2556). Narrow, the Task
 * column trims, then Required, Cost /unit and Rate unit fold away while
 * Input name would drop below 14 rem (their values move under the product's
 * name), and below 40 rem the Task cell stacks above its products. Wide,
 * Notes becomes a column once Input name has its 28 rem and Notes 16 rem,
 * and the sheet stops growing (TaskCategorySheet), so the numbers stay
 * beside their product.
 * `widthRem` of 0 means unmeasured, treated as roomy.
 */
export const computeSheetLayout = (
  widthRem: number,
  preferences: TasksViewPreferences,
  isProductionFilter: boolean
): SheetLayout => {
  const width = widthRem > 0 ? widthRem : Infinity;
  const isStacked = width < STACK_BELOW_REM;
  const isTrimmed =
    !isStacked &&
    (preferences.taskColumnWidth === 'trimmed' ||
      (preferences.taskColumnWidth === 'auto' &&
        width < TRIM_TASK_COLUMN_BELOW_REM));
  const taskRem = isStacked
    ? 0
    : isTrimmed
      ? SHEET_COLUMN_REM.taskTrimmed
      : SHEET_COLUMN_REM.taskFull;

  const isCompact = !isStacked && preferences.taskColumnWidth === 'trimmed';

  if (isProductionFilter) {
    return {
      isStacked,
      isTrimmed,
      isCompact,
      columns: {
        rateUnit: true,
        required: true,
        costPerUnit: true,
        costPerHa: true,
      },
      folded: { rateUnit: false, required: false, costPerUnit: false },
      notesAsColumn: false,
    };
  }

  const columns = isStacked
    ? {
        ...preferences.columns,
        required: false,
        costPerUnit: false,
        rateUnit: false,
      }
    : foldColumnsForWidth(width - taskRem, preferences.columns);
  const folded: FoldedColumns = {
    rateUnit: preferences.columns.rateUnit && !columns.rateUnit,
    required: preferences.columns.required && !columns.required,
    costPerUnit: preferences.columns.costPerUnit && !columns.costPerUnit,
  };

  const notesAsColumn =
    !isStacked &&
    preferences.rows.notes &&
    width >= taskRem + valueColumnsRem(columns) + NAME_MAX_REM + NOTES_MIN_REM;

  return { isStacked, isTrimmed, isCompact, columns, folded, notesAsColumn };
};
